/**
 * Sample data for the screens the design specifies but the API does not serve yet.
 *
 * Every figure here is transcribed from the Biovet Accounting design file, so the
 * screens read as one book: the same six SKUs, the same five customers, the same
 * FX rates, the same July 2026 period. It is deliberately **not** wired to
 * PostgreSQL — the five voucher forms, the chart of accounts and the four
 * financial statements read real data and must stay that way. These are the
 * registers and reports around them, drawn so the shape of each screen is settled
 * before its endpoint exists.
 *
 * When a screen graduates to a real query, delete its entry here rather than
 * leaving a fallback: a report that silently shows sample figures is worse than
 * one that says it has none.
 */

/** A grid column, in the design's own vocabulary. */
export interface RecordColumn {
  label: string;
  /** Fixed width, e.g. '140px'. Omit to flex. */
  width?: string;
  align?: 'left' | 'right';
  /** Tabular figures — amounts, dates, document numbers. */
  mono?: boolean;
  /** Render the cell as a status badge rather than text. */
  badge?: boolean;
}

/**
 * A row is either plain cells, or cells with a role.
 *
 * `head` is a section label (Revenue, Cost of sales), `bold` a subtotal, and
 * `emphasis` the one line a reader's eye should land on — net profit, total
 * assets. The design carries all three and a statement is unreadable without
 * them.
 */
export type RecordRow =
  string[] | { c: string[]; head?: boolean; bold?: boolean; emphasis?: boolean };

export interface RecordSet {
  crumb: string;
  title: string;
  subtitle: string;
  /** Filter chips. The first is selected; they are presentational for now. */
  chips: string[];
  cols: RecordColumn[];
  rows: RecordRow[];
  /** Footer row, rendered outside the scroller so it survives virtualization. */
  total?: string[];
}

const R = 'right' as const;

// ─────────────────────────────────────────────────────────────────────────────
// Finance, party and management reports
// ─────────────────────────────────────────────────────────────────────────────

// -----------------------------------------------------------------------------
// Bank reconciliation: deleted, because the screen reads PostgreSQL now
// -----------------------------------------------------------------------------
//
// `FINANCE.recon` lived here — eight transcribed statement lines, a subtitle
// claiming "41 lines · 37 auto-matched" and an `Unmatched (4)` chip whose 4 was
// a character in a string. `pages/BankReconciliation.tsx` reads
// GET /api/bank-reconciliation, which answers the statement, its lines, the
// per-status counts and the difference against the GL in one request.
//
// Deleted rather than left as a fallback, per the rule at the top of this file.
// A bank reconciliation is the one report where invented figures are worst: the
// whole point of it is that the difference is real.

// -----------------------------------------------------------------------------
// Sales by zone, gross margin and the audit trail: deleted, because these read
// PostgreSQL now
// -----------------------------------------------------------------------------
//
// FINANCE.saleszone, FINANCE.margin and FINANCE.audit lived here: six invented
// salespeople against invented targets, six SKUs with a margin nobody had ever
// earned, and an audit trail of edits that never happened -- complete with IP
// addresses. `pages/ManagementReports.tsx` reads GET /api/reports/sales-by-zone,
// /gross-margin and /audit-trail, all three windowed by the chips.
//
// The audit trail was the worst of the three to leave standing. A compliance
// report whose whole claim is that it records what really happened, showing
// figures somebody typed into a design file, is not a weaker version of the
// real thing -- it is the opposite of it.

// ─────────────────────────────────────────────────────────────────────────────
// Brokerage — Part 1. Goods never touch the GL; only commission posts.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Sales and purchase — Part 2. The company owns the goods here.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every grid still fed by sample data, by nav item key.
 *
 * **It is empty**, and that is the point it has been working towards: every
 * screen in the application now builds its own `RecordSet` from rows the API
 * returned. `RecordGrid` in `pages/Records.tsx` is still the shared chrome —
 * one grid, one set of chips, one print layout — but nothing renders through
 * `RecordsScreen`'s lookup any more.
 *
 * Kept rather than deleted outright because it is the seam a new screen is
 * drawn against: sketch the grid here, agree the columns, then write the query
 * and take the entry back out. `RecordsScreen` still reports a missing key
 * loudly rather than rendering blank.
 */
export const RECORD_SETS: Record<string, RecordSet> = {} as Record<string, RecordSet>;

// -----------------------------------------------------------------------------
// Dashboard: deleted, because Overview reads PostgreSQL now
// -----------------------------------------------------------------------------
//
// KPIS, CASH_BARS, QUEUES, RECENT_POSTINGS, STOCK_AT_RISK,
// TRIAL_BALANCE_CHECK, BROKERAGE_LINE and TRADING_LINE lived here and are
// gone. `pages/Dashboard.tsx` reads GET /api/dashboard, which answers all
// eight panels in one request so they cannot disagree with each other.
//
// The `Kpi` and `QueueItem` interfaces went with them: the shapes now live
// in `lib/api.ts` beside the endpoint that returns them.

// ─────────────────────────────────────────────────────────────────────────────
// Sales invoice
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Import costing sheet
// ─────────────────────────────────────────────────────────────────────────────
// -----------------------------------------------------------------------------
// Users, zones and setup: deleted, because these screens read PostgreSQL now
// -----------------------------------------------------------------------------
//
// ORG, PERMS, PERM_COLS, ZONES, COMPANY_PROFILE, PERIODS, NUMBERING and
// TAX_FX lived here and are gone. `pages/UsersZones.tsx` reads /admin/users,
// /admin/roles and /admin/zones; `pages/Setup.tsx` reads /setup/company,
// /setup/calendar, /setup/numbering and /setup/tax-fx, plus the three master
// registers. All of them write as well as read.
//
// Deleted rather than left as a fallback, per the rule in CLAUDE.md: a screen
// that silently shows sample figures when its query fails is worse than one
// that says it has none, and a permission matrix of invented ticks is worse
// than either -- somebody would believe it.

// ─────────────────────────────────────────────────────────────────────────────
// Report library
// ─────────────────────────────────────────────────────────────────────────────

export const REPORT_GROUPS: Array<{ title: string; items: string[] }> = [
  {
    title: 'Financial',
    items: [
      'Day Book',
      'Cash Book',
      'Bank Book',
      'General Ledger',
      'Trial Balance',
      'Profit & Loss',
      'Balance Sheet',
      'Cash Flow (indirect)',
      'Journal Register',
    ],
  },
  {
    title: 'Party',
    items: [
      'Supplier Ledger',
      'Customer Ledger',
      'AR Ageing',
      'AP Ageing',
      'Party Balance Summary',
      'Overdue & Follow-up',
    ],
  },
  {
    title: 'Purchase & Import',
    items: [
      'Purchase Register',
      'Import Costing Sheet',
      'Landed Cost Analysis',
      'Purchase Return Register',
      'Country / Port-wise Volume',
      'FX Gain–Loss on Imports',
    ],
  },
  {
    title: 'Inventory',
    items: [
      'Stock Ledger',
      'Stock Summary (warehouse-wise)',
      'Valuation Report',
      'Batch & Expiry',
      'Reorder Report',
      'Slow & Non-moving',
      'Movement Analysis',
      'Tally Variance',
    ],
  },
  {
    title: 'Sales',
    items: [
      'Sales Register',
      'Sales by Product',
      'Sales by Customer',
      'Sales by Zone',
      'Sales by Salesperson',
      'Gross Margin Analysis',
      'Sale Return Register',
      'Target vs Achievement',
    ],
  },
  {
    title: 'Commission',
    items: [
      'Salesperson Commission Statement',
      'Accrued vs Paid',
      'Slab Achievement',
      'Zone-wise Payout',
    ],
  },
  {
    title: 'Brokerage',
    items: [
      'Deal Register',
      'Deal Pipeline Ageing',
      'Pending Settlement',
      'Commission Receivable Ageing',
      'Supplier Performance',
      'Buyer Performance',
      'Import Deal Tracker',
      'Sub-agent Statement',
    ],
  },
  {
    title: 'Management',
    items: [
      'Zone-wise Profitability',
      'Product Profitability',
      'Cost-Center P&L',
      'Budget vs Actual',
      'KPI Dashboard',
    ],
  },
  {
    title: 'Compliance',
    items: [
      'Sales Tax Register – Output',
      'Sales Tax Register – Input',
      'WHT Register',
      'Audit Trail Report',
    ],
  },
];
